import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  HiChevronDown, 
  HiCheck, 
  HiOutlineUser, 
  HiOutlineMail, 
  HiOutlinePhone, 
  HiOutlineLocationMarker, 
  HiOutlineOfficeBuilding, 
  HiOutlineMailOpen,
  HiSearch 
} from 'react-icons/hi';
import { motion, AnimatePresence } from 'framer-motion';

const CustomDropdown = ({ 
  name, 
  value, 
  onChange, 
  options, 
  placeholder = "Select...", 
  searchPlaceholder = "Search...",
  error = false,
  icon: Icon,
  valueKey = "value",
  labelKey = "label"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Filter options based on search term
  const filteredOptions = options.filter(option =>
    option[labelKey].toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get selected option for display
  const selectedOption = options.find(option => option[valueKey] === value);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (option) => {
    onChange({
      target: {
        name: name,
        value: option[valueKey]
      }
    });
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchTerm('');
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={handleToggle}
        className={`pl-10 pr-10 block w-full py-3 border ${
          error
            ? 'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500'
            : 'border-gray-300 focus:ring-[#174540] focus:border-[#174540]'
        } rounded-md shadow-sm text-gray-900 sm:text-sm transition-all text-left bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2`}
      >
        {/* Icon */}
        {Icon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Icon className="h-5 w-5 text-gray-400" />
          </div>
        )}
        
        {/* Selected Value */}
        <span className={selectedOption ? 'text-gray-900' : 'text-gray-400'}>
          {selectedOption ? selectedOption[labelKey] : placeholder}
        </span>
        
        {/* Dropdown Arrow */}
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
          <HiChevronDown 
            className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${
              isOpen ? 'transform rotate-180' : ''
            }`} 
          />
        </div>
      </button>

      {/* Dropdown Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden"
          >
            {/* Search Input - Fixed at top */}
            <div className="sticky top-0 bg-white border-b border-gray-200 p-3">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <HiSearch className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="pl-9 block w-full py-2 border border-gray-300 rounded-md focus:ring-[#174540] focus:border-[#174540] sm:text-sm"
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>

            {/* Options List - Scrollable */}
            <div className="max-h-48 overflow-y-auto">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <button
                    key={option[valueKey]}
                    type="button"
                    onClick={() => handleSelect(option)}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none transition-colors ${
                      value === option[valueKey] 
                        ? 'bg-[#174540] text-white hover:bg-[#0f2e2a]' 
                        : 'text-gray-900'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>{option[labelKey]}</span>
                      {value === option[valueKey] && (
                        <HiCheck className="h-4 w-4 text-white" />
                      )}
                    </div>
                  </button>
                ))
              ) : (
                <div className="px-4 py-3 text-gray-500 text-sm">
                  Aucun résultat trouvé
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CustomDropdown;